/**
 * 取り込みの記録1件（issue #89・docs/01 6-4「結果はあとから何度でも見返せます」）。
 *
 * そのときの件数と、取り込めなかった行（何行目・どの項目・なぜ・どう直すか）を出します。
 * CSVの中身そのものは残していません（お預かりしないため）。
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useApi } from '../api/context.js';
import type { RosterColumnsResponse, RosterImportRecord } from '../api/types.js';
import { formatDateTime } from '../members/labels.js';
import { ErrorBlock, LoadingBlock } from '../components/StatusBlock.js';
import { loadFailure, type LoadFailure } from '../api/load-failure.js';
import { RosterErrorTable } from '../components/RosterErrorTable.js';
import { RosterSummaryList } from '../components/RosterSummaryList.js';
import { Card } from '../components/ui/Card.js';
import { PageHeader } from '../components/ui/PageHeader.js';

export function RosterImportDetailPage(): ReactNode {
  const api = useApi();
  const { id = '' } = useParams();
  const [record, setRecord] = useState<RosterImportRecord | null>(null);
  const [columns, setColumns] = useState<RosterColumnsResponse | null>(null);
  const [error, setError] = useState<LoadFailure | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setRecord(null);
    setError(null);
    api
      .get<RosterImportRecord>(`/admin/roster/imports/${id}`)
      .then((loaded) => {
        if (!cancelled) setRecord(loaded);
      })
      .catch((caught: unknown) => {
        if (!cancelled) {
          setError(loadFailure(caught));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [api, id, reloadKey]);

  useEffect(() => {
    let cancelled = false;
    api
      .get<RosterColumnsResponse>('/admin/roster/columns')
      .then((loaded) => {
        if (!cancelled) setColumns(loaded);
      })
      .catch(() => {
        if (!cancelled) setColumns({ columns: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [api]);

  return (
    <article>
      <PageHeader
        title="取り込みの記録"
        description={record ? formatDateTime(record.createdAt) : undefined}
        actions={
          <Link className="ui-button ui-button-secondary" to="/roster/imports">
            記録の一覧に戻る
          </Link>
        }
      />

      {error ? <ErrorBlock failure={error} onRetry={() => setReloadKey((n) => n + 1)} /> : null}
      {!error && !record ? <LoadingBlock /> : null}

      {record ? (
        <Card
          title={
            record.dryRun ? '取り込む前の確認（メンバーの登録内容は変えていません）' : '取り込み'
          }
          description={`ファイル：${record.sourceName ?? '（名前なし）'}`}
        >
          {record.summary ? (
            <RosterSummaryList summary={record.summary} />
          ) : (
            <p>件数の記録が残っていません。</p>
          )}
          <RosterErrorTable issues={record.errors ?? []} columns={columns?.columns ?? []} />
          {record.deactivationCandidates && record.deactivationCandidates.length > 0 ? (
            <section>
              <h3>CSVに載っていなかった方（{record.deactivationCandidates.length}名）</h3>
              <p>退職された方であれば、メンバーの画面でお一人ずつ無効にしてください。</p>
              <ul>
                {record.deactivationCandidates.map((candidate) => (
                  <li key={candidate.userId}>{candidate.displayName}</li>
                ))}
              </ul>
            </section>
          ) : null}
        </Card>
      ) : null}
    </article>
  );
}
